import React, { FC, useCallback, useContext, useEffect, useState } from 'react';

import { Paper } from '@material-ui/core';

import { GlobalContext } from '../../../store';
import { IFormat, IMetaFormat } from '../../../store/admin/roundFormats/store';
import { IEventEnrollment } from '../../../store/player/events/store';
import { ScoreType } from '../../../store/admin/eventFormats/store';

import BackToTop from '../../../components/BackToTop';
import LeaderboardHeader from '../../../components/LeaderboardHeader/LeaderboardHeader';
import Select from '../../../components/Select';
import { IOption } from '../../../components/Select/Select';
import { getAutoCompleteOptionsArray } from '../../../contrib/Utils/getAutoCompleteOptionsArray';
import { getObjectByIdFromArray } from '../../../contrib/Utils/getObjectByIdFromArray';
import {
  AssignResultsProvider,
  useAssignResultsDispatch,
  useAssignResultsState
} from '../../admin/EventWizard/Common/AssignResults/Context/AssignResults.context';
import {
  EventLeaderboardsProvider,
  useEventLeaderboardsDispatch,
  useEventLeaderboardsState
} from './Context/EventLeaderboards.context';
import { FormatOptions } from './FormatOptions/FormatOptions';
import ResultsTable from './ResultsTable/ResultsTable';
import LeaderboardSelector from './LeaderboardSelector/LeaderboardSelector';
import { ScoreTypeSwitch } from './ScoreTypeSwitch/ScoreTypeSwitch';
import { sortBy } from '../../../contrib/sortBy';
import { getRoundFormatsList } from '../../admin/EventWizard/Common/LeaderboardFilter/Utils/getRoundFormatsList';
import { getFormatsList } from '../../admin/EventWizard/Common/LeaderboardFilter/Utils/getFormatsList';

export interface IProps {
  eventId: number;
  roundId: number;
  holeIndex: number;
}

const EventLeaderboardsComponent: FC<IProps> = props => {
  const { eventId, roundId, holeIndex } = props;

  const {
    actions: {
      adminTeamResults: { get: getTeamResults, getTeamResultsForEvent },
      teamHoleResults: { get: getTeamHoleResults },
      roundResults: { get: getRoundResults }
    },
    events,
    adminRoundFormats,
    adminEventFormats,
    roundResults
  } = useContext(GlobalContext);

  const { dispatch: dispatchAssignResults } = useAssignResultsDispatch();
  const { dispatch: dispatchEventLeaderboards } = useEventLeaderboardsDispatch();

  const {
    selectedFormat,
    selectedTeamFormat,
    formatsList,
    roundFormatsList
  } = useAssignResultsState();

  const { selectedRound, selectedFlightId, isEventSelected } = useEventLeaderboardsState();

  const event = events[eventId];

  const [displayTeamFormats, setDisplayTeamFormats] = useState(false);
  const [selectedFlightOption, setSelectedFlightOption] = useState<IOption>();
  const [flightsOptions, setflightsOptions] = useState<IOption[]>([]);

  const onChangeFlight = useCallback(
    async (flight: IOption) => {
      await getRoundResults(selectedRound ? selectedRound.id : -1, eventId, true);

      setSelectedFlightOption(flight);

      dispatchEventLeaderboards({ type: 'selectFlightId', value: Number(flight.value) });
    },
    [selectedRound, eventId]
  );

  const onChangeFormat = () => {
    if (selectedFlightOption) {
      onChangeFlight(selectedFlightOption).catch();
    }
  };

  useEffect(() => {
    setDisplayTeamFormats(
      !!event &&
        event.allowTeams &&
        Object.values(formatsList).some(format => format.prefix.includes('team'))
    );
  }, [event, formatsList]);

  useEffect(() => {
    if (roundId) {
      getTeamHoleResults(roundId);
    }
  }, [getTeamHoleResults, roundId, holeIndex]);

  useEffect(() => {
    dispatchAssignResults({
      type: 'selectScoreType',
      value: ScoreType.GROSS
    });
  }, []);

  useEffect(() => {
    if (events && event && flightsOptions.length > 0) {
      const flightOption =
        flightsOptions.length === 1
          ? flightsOptions[0]
          : flightsOptions.find(option => String(option.value) === String(event.enrollment.flight));

      if (flightOption) {
        onChangeFlight(flightOption).catch(err => {
          throw new Error(err);
        });
      }
    }
  }, [events, flightsOptions, selectedRound, eventId]);

  useEffect(() => {
    if (event && event.flights && selectedFormat) {
      const selectedFormatCategory = !!selectedTeamFormat ? 't' : 'i';

      const eventFlights = event.flights.filter(
        flight => flight.category === selectedFormatCategory
      );

      const newFlightOptions = getAutoCompleteOptionsArray(sortBy(eventFlights, 'asc', 'id'));

      setflightsOptions(newFlightOptions);

      if (newFlightOptions.length > 1) {
        onChangeFlight(newFlightOptions[0]).catch();
      }
    }
  }, [events, eventId, selectedFormat, selectedTeamFormat]);

  useEffect(() => {
    if (flightsOptions.length > 0) {
      dispatchEventLeaderboards({ type: 'selectFlightId', value: Number(flightsOptions[0].value) });
    }
  }, [flightsOptions]);

  useEffect(() => {
    if (Object.keys(roundResults).length > 0) {
      const selectedRoundId = selectedRound ? Number(selectedRound.id) : -1;
      const selectedRoundResults = roundResults[selectedRoundId];

      const newResults =
        selectedRoundResults && selectedRoundResults[selectedFlightId]
          ? selectedRoundResults[selectedFlightId]
          : [];

      dispatchAssignResults({
        type: 'setResults',
        value: newResults
      });
    }
  }, [selectedRound, roundResults, selectedFlightId]);

  useEffect(() => {
    if (events && eventId && event) {
      dispatchEventLeaderboards({
        type: 'setEnrollments',
        value: getObjectByIdFromArray<IEventEnrollment>(event.enrollments)
      });
    }
  }, [events, eventId]);

  useEffect(() => {
    const minutesToReload = 50;

    const timer = setTimeout(() => window.location.reload(false), minutesToReload * 60 * 1000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!selectedFormat) {
      const strokeplayFormat: IFormat | undefined = Object.values(formatsList).find(
        (format: IFormat) => format.prefix === 'stroke'
      );
      const formatToSelect: IFormat = strokeplayFormat
        ? strokeplayFormat
        : Object.values(formatsList)[0];

      if (formatToSelect) {
        dispatchAssignResults({
          type: 'selectFormat',
          value: formatToSelect.id
        });
      }
    }
  }, [selectedFormat, formatsList]);

  useEffect(() => {
    if (!!event && !isEventSelected && !selectedRound) {
      dispatchEventLeaderboards({
        type: 'setSelectedRound',
        value: event.rounds.slice(-1)[0]
      });
    }
  }, [events, eventId]);

  useEffect(() => {
    if (!!selectedRound) {
      getTeamResults('round', selectedRound.id, true);
    }
  }, [selectedRound]);

  useEffect(() => {
    if (isEventSelected) {
      getTeamResultsForEvent('event', eventId, true);
    }
  }, [eventId, isEventSelected]);

  useEffect(() => {
    if (
      selectedRound &&
      selectedRound.id &&
      adminRoundFormats &&
      adminRoundFormats[Number(selectedRound.id)]
    ) {
      dispatchAssignResults({
        type: 'setRoundFormatsList',
        value: getRoundFormatsList(adminRoundFormats[Number(selectedRound.id)])
      });
    }
  }, [adminRoundFormats, selectedRound]);

  useEffect(() => {
    if (isEventSelected && eventId && adminEventFormats && adminEventFormats[eventId]) {
      dispatchAssignResults({
        type: 'setEventFormatsList',
        value: getRoundFormatsList(adminEventFormats[eventId])
      });
    }
  }, [adminEventFormats, eventId, isEventSelected]);

  useEffect(() => {
    const roundFormats: IMetaFormat[] = selectedRound
      ? selectedRound.formats
      : event && event.rounds
      ? event.formats
      : [];

    dispatchAssignResults({
      type: 'setRoundFormatsList',
      value: getRoundFormatsList(roundFormats)
    });
  }, [selectedRound, event, selectedRound, eventId]);

  useEffect(() => {
    dispatchAssignResults({
      type: 'setFormatsList',
      value: getFormatsList(Object.values(roundFormatsList))
    });
  }, [roundFormatsList]);

  return (
    <div
      className={`home event-leaderboards wrapper ${
        selectedFormat && selectedFormat.prefix === 'stroke' ? 'heightFix' : ''
      }`}
    >
      <Paper className="paper-full-width">
        {/* Disabled until multiround events is enabled again  */}
        {false && <LeaderboardSelector eventId={eventId} roundId={roundId} holeIndex={holeIndex} />}

        {Object.keys(formatsList).length > 0 ? (
          <>
            <LeaderboardHeader>
              <div className="bottom-space">
                {flightsOptions.length > 1 && (
                  <Select
                    title="FLIGHTS"
                    defaultOption={selectedFlightOption}
                    options={flightsOptions}
                    onSelect={onChangeFlight}
                  />
                )}
              </div>

              {(!selectedRound || (selectedRound && selectedRound.id)) && (
                <FormatOptions
                  formatId={selectedFormat ? selectedFormat.id : undefined}
                  displayTeamFormats={displayTeamFormats}
                  eventId={eventId}
                  roundId={roundId}
                  holeIndex={holeIndex}
                  onChangeFormat={onChangeFormat}
                />
              )}
            </LeaderboardHeader>

            {(!!selectedRound || isEventSelected) && selectedFlightId > 0 && selectedFormat ? (
              <>
                {selectedFormat.prefix !== 'gbn' && <ScoreTypeSwitch />}

                <ResultsTable />
              </>
            ) : (
              <p>
                {`Please select ${[
                  `${!selectedRound ? 'Leaderboard' : ''}`,
                  `${selectedFlightId <= 0 ? 'Flight' : ''}`,
                  `${!selectedFormat ? 'Format' : ''}`
                ]
                  .filter(Boolean)
                  .join(', ')}`}
              </p>
            )}
          </>
        ) : (
          <p>There are no Leaderboards for this selection</p>
        )}

        <div className="back-to-top-container">
          <BackToTop scrollStepInPx={50} delayInMs={16.66} />
        </div>
      </Paper>
    </div>
  );
};

const EventLeaderboards = (props: IProps) => (
  <AssignResultsProvider>
    <EventLeaderboardsProvider>
      <EventLeaderboardsComponent {...props} />
    </EventLeaderboardsProvider>
  </AssignResultsProvider>
);

export default EventLeaderboards;
